import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Input, Autocomplete, Card, Stack } from '@/components/ui';
import { useItineraryFormOptions } from '@/hooks/useItineraryFormOptions';
import { getWriteErrorMessage } from '@/utils/apiError';
import type { CreateItineraryRequest } from '@/types/cargoList.types';
import { CargoDetailsService } from '@/services/cargoDetailsService';
import { ArrowRightLeft, Calendar, MapPin } from 'lucide-react';

interface AddItineraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  cargoId: number;
  existingItinerary: Array<{ order: number }>;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  purpose: Yup.string().required('Purpose is required'),
  port: Yup.string().required('Port is required'),
  via: Yup.string().required('Via is required'),
  arrivalDate: Yup.string().required('Arrival date is required'),
  arrivalTime: Yup.string().required('Arrival time is required'),
  departureDate: Yup.string().required('Departure date is required'),
  departureTime: Yup.string().required('Departure time is required'),
  speed: Yup.number()
    .required('Speed is required')
    .min(1, 'Speed must be greater than 0')
    .max(50, 'Speed cannot exceed 50 knots'),
});

export function AddItineraryModal({
  isOpen,
  onClose,
  cargoId,
  existingItinerary,
  onSuccess,
}: AddItineraryModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Calculate the next order value based on existing itinerary
  const nextOrder =
    existingItinerary.length > 0 ? Math.max(...existingItinerary.map((item) => item.order)) + 1 : 1;

  const {
    portOptions,
    purposeOptions,
    viaPointOptions,
    searchPorts,
    searchViaPoints,
    loading: optionsLoading,
    error: optionsError,
  } = useItineraryFormOptions();

  const initialValues = {
    purpose: '',
    port: '',
    via: '',
    arrivalDate: '',
    arrivalTime: '',
    departureDate: '',
    departureTime: '',
    speed: 12,
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmitError(null);

    try {
      // The Autocomplete sets the value to option.id on selection. Look up by id
      // first; fall back to name in case the user typed and matched by name.
      const matchOption = <T extends { id: number | string; name: string }>(
        options: T[],
        value: unknown
      ) => options.find((o) => String(o.id) === String(value) || o.name === value);

      const selectedPort = matchOption(portOptions, values.port);
      const selectedPurpose = matchOption(purposeOptions, values.purpose);
      const selectedVia = matchOption(viaPointOptions, values.via);

      if (!selectedPort) throw new Error('Selected port not found');
      if (!selectedPurpose) throw new Error('Selected purpose not found');
      if (!selectedVia) throw new Error('Selected via point not found');

      // Parse date+time as local time (no trailing 'Z') so the value the user
      // typed matches what they see rendered. toISOString converts to UTC for the API.
      const arrivalTime = new Date(`${values.arrivalDate}T${values.arrivalTime}:00`);
      const departureTime = new Date(`${values.departureDate}T${values.departureTime}:00`);

      const createRequest: CreateItineraryRequest = {
        FixtureId: cargoId,
        PurposeId: Number(selectedPurpose.id),
        PortId: Number(selectedPort.id),
        ViaId: Number(selectedVia.id),
        ArrivalTime: arrivalTime.toISOString(),
        DepartureTime: departureTime.toISOString(),
        Speed: values.speed,
        Order: nextOrder,
      };

      await CargoDetailsService.createItineraryItem(createRequest);

      onSuccess?.();
      onClose();
    } catch (err) {
      setSubmitError(getWriteErrorMessage(err, 'Failed to create itinerary item'));
    } finally {
      setSubmitting(false);
    }
  };
  if (optionsLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Add Itinerary Item" size="lg">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading form options...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Add Itinerary Item" isOpen={isOpen} onClose={onClose}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-6">
            {(submitError || optionsError) && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {submitError || optionsError}
                </p>
              </div>
            )}

            {/* Location & Purpose Section */}
            <Card title="Location & Purpose" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <MapPin className="mr-1 inline h-4 w-4" />
                      Port
                    </label>
                    <Autocomplete
                      name="port"
                      options={portOptions}
                      onSearch={searchPorts}
                      loading={optionsLoading}
                      allowCustomValue={false}
                      placeholder="Select or search for a port..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Purpose
                    </label>
                    <Autocomplete
                      name="purpose"
                      options={purposeOptions}
                      loading={optionsLoading}
                      allowCustomValue={false}
                      placeholder="Select purpose..."
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <ArrowRightLeft className="mr-1 inline h-4 w-4" />
                    Via
                  </label>
                  <Autocomplete
                    name="via"
                    options={viaPointOptions}
                    onSearch={searchViaPoints}
                    loading={optionsLoading}
                    allowCustomValue={false}
                    placeholder="Select or search for via point..."
                  />
                </div>
              </Stack>
            </Card>

            {/* Navigation Details Section */}
            <Card title="Navigation Details" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Speed (knots)
                  </label>
                  <Input
                    name="speed"
                    type="number"
                    min="1"
                    max="50"
                    step="0.1"
                    placeholder="Enter speed..."
                  />
                </div>
              </Stack>
            </Card>

            {/* Schedule Section */}
            <Card title="Schedule" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Calendar className="mr-1 inline h-4 w-4" />
                    Arrival
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input name="arrivalDate" type="date" placeholder="Select date..." />
                    <Input name="arrivalTime" type="time" placeholder="Select time..." />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Calendar className="mr-1 inline h-4 w-4" />
                    Departure
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input name="departureDate" type="date" placeholder="Select date..." />
                    <Input name="departureTime" type="time" placeholder="Select time..." />
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={optionsLoading}>
                Add
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
